import axios from 'axios'
import { defineStore } from 'pinia'
import { toast } from 'vue3-toastify'
import ErrorMessage from '@/constant/ErrorMessage'
import Urls from '@/constant/Urls'
import { router } from '@/plugins/router'

// Configure axios globally (optional, but helpful)
axios.defaults.withCredentials = true
axios.defaults.headers.common['Accept'] = 'application/json'

export const useAuthStore = defineStore('auth', {

  state: () => ({
    email: '',
    password: '',
    remember: false,
    authStatus: !!localStorage.getItem('auth_token'),
    accountType: localStorage.getItem('accountType') || null,
    profileAlert: '',
    user: null as any,
    isProfileLoaded: false,
    firstName: '',
    lastName: '',
    gender: '',
    phoneNumber: '',
    password_confirmation: '',
    userType: '',
  }),

  getters: {
    isAuthenticated: (state) => state.authStatus,
    currentUser: (state) => state.user,
    getProfileAlert: (state) => state.profileAlert,
  },

  actions: {
    /** Login 
     * Note: This method now only handles the initial login step (email/password + CSRF).
     * The OTP verification is handled separately in verifyLoginOtp() for better flow control.
    */
    async login() {
      try {
        // This is for local development.
        // await axios.get('http://localhost/auth/generate/csrf-cookie', {

        // This is For production, ensure the URL points to your deployed backend
        await axios.get('https://api-q66a.onrender.com/auth/generate/csrf-cookie', {
          withCredentials: true,
        }).catch(() => {
          toast.error(ErrorMessage.InvalidRequest, { autoClose: false })
          throw new Error('CSRF Cookie setup failed')
        })

        const postOptions = {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            email: this.email,
            password: this.password,
            remember: this.remember,
          }),
        }

        const loginResponse = await fetch(`${Urls.api}auth/login`, postOptions)
        if (!loginResponse.ok) {
          toast.error('Login failed. Please try again.', { autoClose: false })

          return
        }
        const loginJson = await loginResponse.json()

        // check that the user is verified before authentication
        if (loginJson.success) {
          // ✅ Store email so the OTP page knows who to verify
          // No token yet — backend only sent an OTP at this stage
          localStorage.setItem('otp_identifier', this.email)
          localStorage.setItem('otp_type', 'login')

          await router.push('/verify-otp')
          toast.success(loginJson.message, { autoClose: false })
        }
        else {
          toast.error(loginJson.message, { autoClose: false })
        }
      }
      catch (error) {
        toast.error(ErrorMessage.InvalidRequest, { autoClose: false })
      }
    },

    /** -------------------------------
     * REGISTER
     * ------------------------------- */
    async register() {
      try {
        const response = await fetch(`${Urls.api}auth/register`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            // Bind the registration form fields to the request body
            firstName: this.firstName,
            lastName:  this.lastName,
            gender:    this.gender,
            phoneNumber: this.phoneNumber,
            email:    this.email,
            password: this.password,
            password_confirmation: this.password_confirmation,
            userType: this.userType,
          }),
        })

        const json = await response.json()

        if (!response.ok) {
          toast.error(json.message ?? 'Registration failed.', { autoClose: false })
          return
        }

        if (json.success) {
          localStorage.setItem('otp_identifier', this.email)
          localStorage.setItem('otp_type', 'register')     // ← so OTP page knows the context

          await router.push('/verify-otp')
          toast.info(json.message, { autoClose: false })
        } else {
          toast.error(json.message, { autoClose: false })
        }
      } catch (error) {
        toast.error(ErrorMessage.InvalidRequest, { autoClose: false })
      }
    },

    /** -------------------------------
     * VERIFY REGISTRATION OTP
     * ------------------------------- */
    async verifyRegistration(otpCode: string) {
      const identifier = localStorage.getItem('otp_identifier')

      if (!identifier) {
        toast.error('Session expired. Please register again.', { autoClose: false })
        await router.push('/register')
        return
      }

      try {
        const response = await fetch(`${Urls.api}auth/register/verify`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            email:    identifier,
            otp_code: otpCode,
          }),
        })

        const json = await response.json()

        if (!response.ok || !json.success) {
          toast.error(json.message ?? 'Invalid or expired OTP.', { autoClose: false })
          return
        }

        // ✅ Verified — store token and redirect
        localStorage.removeItem('otp_identifier')
        localStorage.removeItem('otp_type')

        // Store the token and account type for authenticated sessions
        localStorage.setItem('accountType', json.data.roles[0])
        localStorage.setItem('auth_token', json.data.auth_token)

        this.authStatus = true
        this.accountType = json.data.roles[0] 

        await router.push('/dashboard')
        toast.success(json.message, { autoClose: false })

      } catch (error) {
        toast.error(ErrorMessage.InvalidRequest, { autoClose: false })
      }
    },

    /** -------------------------------
     * GET USER PROFILE
     * ------------------------------- */
    async getUserProfile() {
      this.isProfileLoaded = false

      const authToken = localStorage.getItem('auth_token')

      if (!authToken) {
        this.authStatus = false
        await router.push('/login')
        toast.warn('Kindly Login to Continue..', { autoClose: false })

        return
      }

      try {
        const { data } = await axios.get(`${Urls.api}auth/my-profile`, {
          headers: { Authorization: `Bearer ${authToken}` },
        })

        if (!data.success) {
          await this.logout()

          return
        }

        // 🔑 CRITICAL FIX: Assign the fetched user object to the store's state
        // Based on your JSON: { success: true, data: { user: { ... } } }
        this.user = data.data.user

        // Use a local variable for cleaner access to the bio
        const userBio = this.user.bio

        if (!userBio.verified) {
          // Redirect for email verification
          this.authStatus = false

          // ✅ OTP flow expects these to know who to verify and why
          localStorage.setItem('otp_identifier', userBio.email)
          localStorage.setItem('otp_type', 'register')
          toast.warn('Your email address is not verified.', { autoClose: false })
          await router.push('/verify-otp')

          return
        }

        // Profile completeness check
        const incompleteProfile
          = !userBio.avatar
          || !userBio.phoneNumber
          || !userBio.residentialAddress
          || !userBio.stateOfResidence
          || userBio.status !== 'confirmed'
          || userBio.verified !== true

        this.profileAlert = incompleteProfile
          ? 'Please complete your profile to access all features!'
          : ''

        this.authStatus = true
        this.accountType = localStorage.getItem('accountType') || this.user.role
      }
      catch (error: any) {
        this.authStatus = false
        localStorage.removeItem('auth_token')
        localStorage.removeItem('accountType')

        const currentRouteName = router.currentRoute.value.name

        // Exclude certain routes from redirection to secure pages
        const isPublicRoute = [
          'home',
          'login',
          'register',
          'forgot-password',
          'testimonial',
          'how-it-works',
          'about',
          'contact',
          'news-feed',
          'privacy-policy',
          'terms-of-service',
        ].includes(currentRouteName)

        if (!isPublicRoute.includes(currentRouteName as string)) {
          await router.push('/login')
          toast.error('Session expired. Please log in again.', { autoClose: false })
        }
        
      }
      finally {
        this.isProfileLoaded = true
      }
    },

    /** -------------------------------
     * LOGOUT
     * ------------------------------- */
    async logout() {
      const authToken = localStorage.getItem('auth_token')

      const clearLocalState = () => {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('accountType')
        localStorage.removeItem('otp_identifier')
        localStorage.removeItem('otp_type')
        this.authStatus  = false
        this.accountType = null
        this.user        = null
      }

      try {
        const response = await fetch(`${Urls.api}auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: 'application/json',
          },
        })

        const jsonDataResponse = await response.json()

        if (response.ok && jsonDataResponse.success) {

          clearLocalState()
          toast.success(jsonDataResponse.message, { autoClose: false })
          await router.push('/login')
          return
          
        }

        // Non-ok response — still log out locally
        clearLocalState()
        toast.warn('Session ended. Please log in again.', { autoClose: false })
        await router.push('/login')

        
      }
      catch (error) {
        // Network failure — log out locally
        clearLocalState()
        toast.warn('Logged out locally. Please log in again.', { autoClose: false })
        await router.push('/login')
      } 
    },

    /** -------------------------------
     * INITIALIZE STATE
     * ------------------------------- */
    initializeFromLocalStorage() {
      const authToken = localStorage.getItem('auth_token')
      const accountType = localStorage.getItem('accountType')

      this.authStatus = !!authToken
      this.accountType = accountType || null
    },

    /** -------------------------------
     * QUICK AUTH CHECK
     * ------------------------------- */
    checkAuth() {
      if (this.authStatus)
        return true

      const token = localStorage.getItem('auth_token')
      if (token) {
        this.authStatus = true

        return true
      }

      return false
    },

    /** -------------------------------
     * VERIFY LOGIN OTP
     * ------------------------------- */
    async verifyLoginOtp(otpCode: string) {
      const identifier = localStorage.getItem('otp_identifier')

      if (!identifier) {
        toast.error('Session expired. Please login again.', { autoClose: false })
        await router.push('/login')
        return
      }

      try {
        const response = await fetch(`${Urls.api}auth/login/verify`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            email: identifier,
            otp_code: otpCode,
          }),
        })

        const json = await response.json()

        if (!response.ok || !json.success) {
          toast.error(json.message ?? 'Invalid or expired OTP.', { autoClose: false })
          return
        }

        // ✅ OTP verified — now we receive the token and user
        localStorage.removeItem('otp_identifier')
        localStorage.removeItem('otp_type')

        // Store the token and account type for authenticated sessions
        localStorage.setItem('auth_token', json.data.auth_token)
        localStorage.setItem('accountType', json.data.roles[0])

        this.authStatus  = true
        this.accountType = json.data.roles[0]

        // Check email verification status before redirecting
        //if (json.data.user.verified) {
          await router.push('/dashboard')
          toast.success(json.message, { autoClose: false })
        //} else {
         // await router.push('/resend-verification-email')
        //}

      } catch (error) {
        toast.error(ErrorMessage.InvalidRequest, { autoClose: false })
      }
    },

    /** -------------------------------
     * RESEND OTP
     * ------------------------------- */
    async resendOtp() {
      const identifier = localStorage.getItem('otp_identifier')
      const type       = localStorage.getItem('otp_type') ?? 'login'

      if (!identifier) {
        toast.error('Session expired. Please login again.', { autoClose: false })
        await router.push('/login')
        return
      }

      try {
        const response = await fetch(`${Urls.api}otp/request`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            identifier,
            type,
          }),
        })

        const json = await response.json()

        if (json.success) {
          toast.success(json.message, { autoClose: false })
        } else {
          // Backend returns this when the 60s cooldown is still active
          toast.warn(json.message, { autoClose: false })
        }
      } catch (error) {
        toast.error('Failed to resend OTP. Please try again.', { autoClose: false })
      }
    },
  },
})
